/* CARILOHA: Bundle configurator web component — driven by bundle_config metaobjects */

import { Component } from '@theme/component';
/* CARILOHA: CartAddEvent (@theme/events) was retired upstream in favor of CartLinesUpdateEvent
   (@shopify/events), which is also what cart-icon.js now listens for to update the cart bubble
   and open the drawer — dispatch it with a deferred promise before the fetch, matching the
   pattern in assets/product-form.js. */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price
 * @property {number} compareAtPrice
 * @property {string[]} options - Ordered list of option values, e.g. ["Queen", "White"]
 * @property {string|null} featuredImage - Variant image URL (120px wide), or null
 * @property {string|null} featuredImageLarge - Variant image URL (600px wide), or null
 */

/**
 * @typedef {Object} BundleProduct
 * @property {number} id
 * @property {string} title
 * @property {string} handle
 * @property {string} featuredImageUrl
 * @property {string[]} options - Ordered list of option names, e.g. ["Size", "Color"]
 * @property {BundleVariant[]} variants
 */

/**
 * @typedef {Object} BundleComponent
 * @property {string} label
 * @property {string} key
 * @property {boolean} required
 * @property {number} defaultQuantity
 * @property {boolean} allowQuantityChange
 * @property {number} displayOrder
 * @property {BundleProduct[]} products
 * @property {Object.<string, string>} [sizeOverrides] - Maps a bundle size label to the size
 *   this component's product actually sells under, when it differs (e.g. {"King": "Split-King"}).
 */

/**
 * @typedef {Object} BundleConfig
 * @property {string} label
 * @property {string} discountType
 * @property {string} discountValue
 * @property {string} discountTiers
 * @property {string} sizeOptionLabel
 * @property {string[]} sizeOptions
 * @property {BundleComponent[]} components
 * @property {number} [solidPresetCount] - pack_presets mode only: how many of a component's
 *   products (in order) get their own solid-color preset card, in addition to the Neutral mix.
 */

/**
 * @typedef {Object} ComponentSelection
 * @property {number} productId
 * @property {string|null} secondaryOption
 * @property {number|null} variantId
 * @property {number} quantity
 */

/**
 * @typedef {Object} BundleState
 * @property {string|null} selectedSize

 * @property {Object.<string, ComponentSelection|null>} selections - null means explicitly skipped (optional only)
 * @property {boolean} loading
 * @property {string|null} error
 * @property {boolean} addedSuccess
 */

/**
 * @typedef {Object} ConfiguratorRefs
 * @property {HTMLElement} configuratorRoot
 * @property {HTMLElement} liveRegion
 */

/** @extends {Component<ConfiguratorRefs>} */
export class BundleConfiguratorComponent extends Component {
  /** @type {BundleConfig|null} */
  #config = null;

  /** Tracks whether the bundle was ready to add on the previous render, to detect the ready transition */
  #wasReady = false;

  /** @type {BundleState} */
  #state = {
    selectedSize: null,
    selections: {},
    loading: false,
    error: null,
    addedSuccess: false,
    // CARILOHA: pack-presets mode only (uiMode === 'pack_presets') — unused by the
    // default step flow, so existing bundles never read or set these.
    pairCount: null,
    colorPreset: null,
  };

  /** @type {Set<string>} Keys of sections that have already played their reveal animation */
  #revealedSections = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  // ─── Initialization ─────────────────────────────────────────────────────────

  #init() {
    const configEl = document.getElementById(this.dataset.configId);
    if (!configEl) {
      console.error('[bundle-configurator] Config script element not found:', this.dataset.configId);
      return;
    }
    try {
      this.#config = JSON.parse(configEl.textContent ?? '');
    } catch (e) {
      console.error('[bundle-configurator] Failed to parse bundle config JSON:', e);
      return;
    }

    // Sort components by displayOrder ascending
    this.#config.components.sort((a, b) => a.displayOrder - b.displayOrder);

    if (this.#config.uiMode === 'pack_presets') {
      this.#initPresetSelections();
      this.#render();
      return;
    }

    // Pre-select any component that has only one product so the user just picks color/size
    const initialSelections = {};
    for (const comp of this.#config.components) {
      if (comp.products.length === 1) {
        initialSelections[comp.key] = {
          productId: comp.products[0].id,
          secondaryOption: null,
          variantId: null,
          quantity: comp.defaultQuantity,
        };
      }
    }
    this.#state = { ...this.#state, selections: initialSelections };

    this.#render();
  }

  /**
   * CARILOHA: pack-presets mode init — defaults to the middle discount tier (e.g. 6 of a
   * 3/6/9 spread), the "mixed" color preset, and the smallest size (first entry in
   * sizeOptions) so a real variant resolves and the price populates immediately on load,
   * instead of waiting on the shopper to pick a size first.
   */
  #initPresetSelections() {
    const tiers = this.#parseTiers();
    const midTier = tiers[Math.floor((tiers.length - 1) / 2)];
    const requiredCount = this.#config.components.filter((c) => c.required).length;
    const pairCount = midTier?.qty ?? requiredCount;
    const colorPreset = 'mixed';
    const selectedSize = this.#config.sizeOptions?.[0] ?? null;
    const selections = this.#buildPresetSelections(pairCount, colorPreset, selectedSize);
    this.#state = { ...this.#state, pairCount, colorPreset, selectedSize, selections };
  }

  // ─── State management ────────────────────────────────────────────────────────

  /** @param {Partial<BundleState>} patch */
  #setState(patch) {
    this.#state = { ...this.#state, ...patch };
    this.#render();
  }

  // ─── Public event handlers (called by Component event delegation) ────────────

  /** Size button clicked. Called by on:click="handleSizeSelect?size=Queen" */
  handleSizeSelect(data) {
    const size = data?.size;
    if (!size || !this.#config) return;

    this.#revealedSections.clear();
    this.#wasReady = false;

    if (this.#config.uiMode === 'pack_presets') {
      // CARILOHA: re-resolve the existing pack-size/color-preset picks against the newly
      // chosen size, rather than the step-mode logic below (which only handles
      // single-product components and would wipe our multi-color pair slots).
      const freshSelections = this.#buildPresetSelections(this.#state.pairCount, this.#state.colorPreset, size);
      this.#setState({ selectedSize: size, selections: freshSelections, error: null, addedSuccess: false });
      return;
    }

    const freshSelections = {};
    for (const comp of this.#config.components) {
      if (comp.products.length === 1) {
        const product = comp.products[0];
        const effSize = this.#effectiveSize(comp, size);
        const secondaryValues = this.#getSecondaryOptionValues(product, effSize);
        if (!secondaryValues) {
          const variantId = this.#resolveVariant(product, effSize, null);
          if (variantId) {
            freshSelections[comp.key] = { productId: product.id, secondaryOption: null, variantId, quantity: comp.defaultQuantity };
          }
        } else {
          freshSelections[comp.key] = { productId: product.id, secondaryOption: null, variantId: null, quantity: comp.defaultQuantity };
        }
      }
    }

    this.#setState({ selectedSize: size, selections: freshSelections, error: null, addedSuccess: false });
    requestAnimationFrame(() => this.#scrollToNextSection());
  }

  /** CARILOHA: pack-presets mode. Called by on:click="handlePairCountSelect?count=6" */
  handlePairCountSelect(data) {
    const count = Number(data?.count);
    if (!count || !this.#config) return;
    const preset = this.#state.colorPreset ?? 'mixed';
    const selections = this.#buildPresetSelections(count, preset, this.#state.selectedSize);
    this.#setState({ pairCount: count, selections, error: null });
  }

  /** CARILOHA: pack-presets mode. Called by on:click="handleColorPresetSelect?preset=mixed" */
  handleColorPresetSelect(data) {
    const preset = data?.preset;
    if (!preset || !this.#config) return;
    const count = this.#state.pairCount ?? this.#config.components.filter((c) => c.required).length;
    const selections = this.#buildPresetSelections(count, preset, this.#state.selectedSize);
    this.#setState({ colorPreset: preset, selections, error: null });
  }

  /** Product button clicked (multi-product component). Called by on:click="handleProductSelect?compKey=sheets&productId=123" */
  handleProductSelect(data) {
    const { compKey, productId } = data ?? {};
    if (!compKey || !productId || !this.#config) return;

    const comp = this.#config.components.find((c) => c.key === compKey);
    const product = comp?.products.find((p) => p.id === productId);
    if (!product) return;

    const effSize = this.#effectiveSize(comp, this.#state.selectedSize);
    const secondaryValues = this.#getSecondaryOptionValues(product, effSize);
    let variantId = null;
    if (!secondaryValues) {
      variantId = this.#resolveVariant(product, effSize, null);
    }

    const newSelections = {
      ...this.#state.selections,
      [compKey]: { productId, secondaryOption: null, variantId, quantity: comp.defaultQuantity },
    };

    this.#setState({ selections: newSelections });
    if (variantId) {
      setTimeout(() => requestAnimationFrame(() => this.#scrollToNextSection()), 350);
    }
  }

  /** Secondary option (color/type) selected. Called by on:click="handleSecondarySelect?compKey=sheets&value=White" */
  handleSecondarySelect(data) {
    const { compKey, value } = data ?? {};
    if (!compKey || !value || !this.#config) return;

    const comp = this.#config.components.find((c) => c.key === compKey);
    const existing = this.#state.selections[compKey];
    if (!comp || !existing) return;

    const product = comp.products.find((p) => p.id === existing.productId);
    if (!product) return;

    const variantId = this.#resolveVariant(product, this.#effectiveSize(comp, this.#state.selectedSize), value);

    const newSelections = {
      ...this.#state.selections,
      [compKey]: { ...existing, secondaryOption: value, variantId },
    };

    this.#setState({ selections: newSelections });
    if (variantId) {
      setTimeout(() => requestAnimationFrame(() => this.#scrollToNextSection()), 380);
    }
  }

  /** Skip an optional component. Called by on:click="handleSkipComponent?compKey=duvet" */
  handleSkipComponent(data) {
    const { compKey } = data ?? {};
    if (!compKey || !this.#config) return;

    const comp = this.#config.components.find((c) => c.key === compKey);
    if (!comp || comp.required) return;

    const newSelections = { ...this.#state.selections, [compKey]: null };
    this.#setState({ selections: newSelections });
  }

  /** Re-open a completed section for editing. Called by on:click="handleEditSection?compKey=sheets" */
  handleEditSection(data) {
    const { compKey } = data ?? {};
    const existing = this.#state.selections[compKey];
    if (!existing) return;

    // Keep product selected but clear the resolved variant so the section opens
    const newSelections = {
      ...this.#state.selections,
      [compKey]: { ...existing, secondaryOption: null, variantId: null },
    };
    this.#setState({ selections: newSelections });

    requestAnimationFrame(() => {
      const el = this.querySelector(`[data-comp="${compKey}"]`);
      this.#smoothScrollTo(el);
    });
  }

  /** Restore a skipped optional component. Called by on:click="handleRestoreComponent?compKey=duvet" */
  handleRestoreComponent(data) {
    const { compKey } = data ?? {};
    if (!compKey || !this.#config) return;

    const comp = this.#config.components.find((c) => c.key === compKey);
    if (!comp) return;

    // Remove the explicit null so the step shows as unselected (not skipped)
    const newSelections = { ...this.#state.selections };
    delete newSelections[compKey];

    this.#setState({ selections: newSelections });
  }

  /** Quantity decrement. Called by on:click="handleQuantityDecrement?compKey=pillows" */
  handleQuantityDecrement(data) {
    const { compKey } = data ?? {};
    const existing = this.#state.selections[compKey];
    if (!existing || existing.quantity <= 1) return;
    this.#setState({
      selections: {
        ...this.#state.selections,
        [compKey]: { ...existing, quantity: existing.quantity - 1 },
      },
    });
  }

  /** Quantity increment. Called by on:click="handleQuantityIncrement?compKey=pillows" */
  handleQuantityIncrement(data) {
    const { compKey } = data ?? {};
    const existing = this.#state.selections[compKey];
    if (!existing) return;
    this.#setState({
      selections: {
        ...this.#state.selections,
        [compKey]: { ...existing, quantity: existing.quantity + 1 },
      },
    });
  }

  /** Add bundle to cart button. Called by on:click="handleAddToCart" */
  handleAddToCart() {
    if (this.#state.loading) return;
    if (!this.#validateRequiredSelections()) return;
    this.#addBundleToCart();
  }

  // ─── Variant resolution helpers ──────────────────────────────────────────────

  /**
   * Returns the index of the size option in a product's options array, or -1.
   * @param {BundleProduct} product
   * @returns {number}
   */
  #getSizeOptionIndex(product) {
    if (!this.#config?.sizeOptionLabel) return -1;
    return product.options.findIndex(
      (o) => o.toLowerCase() === this.#config.sizeOptionLabel.toLowerCase()
    );
  }

  /**
   * Returns the index of the secondary option (any option that is not the size option), or -1.
   * @param {BundleProduct} product
   * @returns {number}
   */
  #getSecondaryOptionIndex(product) {
    const sizeIdx = this.#getSizeOptionIndex(product);
    return product.options.findIndex((_, i) => i !== sizeIdx);
  }

  /**
   * Returns unique secondary option values available for the given size, or null if no secondary option.
   * @param {BundleProduct} product
   * @param {string|null} selectedSize
   * @returns {string[]|null}
   */
  #getSecondaryOptionValues(product, selectedSize) {
    const secondaryIdx = this.#getSecondaryOptionIndex(product);
    if (secondaryIdx === -1) return null;

    const sizeIdx = this.#getSizeOptionIndex(product);
    const filtered = product.variants.filter((v) => {
      if (!selectedSize) return true;
      if (sizeIdx === -1) return true;
      return this.#sizeMatches(v.options[sizeIdx], selectedSize);
    });

    return [...new Set(filtered.map((v) => v.options[secondaryIdx]))];
  }

  /**
   * CARILOHA: whether a component has any real alternative to switch to — more than one
   * product, or a secondary option with more than one value for the current size. A component
   * with neither (e.g. a pillow available in a single size/color) has nothing to "change", so
   * its completed chip shouldn't offer a Change action that just clears and re-requires the
   * same single selection.
   * @param {BundleComponent} comp
   * @param {string|null} effSize
   * @returns {boolean}
   */
  #hasAlternativeChoice(comp, effSize) {
    if (comp.products.length > 1) return true;
    const product = comp.products[0];
    if (!product) return false;
    const secondaryValues = this.#getSecondaryOptionValues(product, effSize);
    return !!secondaryValues && secondaryValues.length > 1;
  }

  /**
   * Returns the name of the secondary option, or null.
   * @param {BundleProduct} product
   * @returns {string|null}
   */
  #getSecondaryOptionName(product) {
    const idx = this.#getSecondaryOptionIndex(product);
    return idx === -1 ? null : product.options[idx];
  }

  /**
   * Returns true if a secondary option name represents a visual color choice (rendered as
   * image swatches) rather than a text attribute like mattress "Firmness Option" (Soft/Firm),
   * which is rendered as text pills instead since a color swatch wouldn't be meaningful.
   * @param {string|null} optionName
   * @returns {boolean}
   */
  #isColorOption(optionName) {
    return !!optionName && optionName.toLowerCase().includes('color');
  }

  /**
   * Returns the featured image URL for the first variant matching the given secondary option value
   * and current size, or null if no image is available.
   * @param {BundleProduct} product
   * @param {string|null} selectedSize
   * @param {string} value - Secondary option value (e.g. "White")
   * @param {'small'|'large'} [size='small']
   * @returns {string|null}
   */
  #getSwatchImage(product, selectedSize, value, size = 'small') {
    const secondaryIdx = this.#getSecondaryOptionIndex(product);
    const sizeIdx = this.#getSizeOptionIndex(product);
    if (secondaryIdx === -1) return null;

    const variant = product.variants.find((v) => {
      if (selectedSize && sizeIdx !== -1 && !this.#sizeMatches(v.options[sizeIdx], selectedSize)) return false;
      return v.options[secondaryIdx] === value;
    });
    return (size === 'large' ? variant?.featuredImageLarge : variant?.featuredImage) ?? null;
  }

  /**
   * Returns the featured image URL for the currently selected variant in a component, or the
   * product's featured image if no variant is resolved yet.
   * @param {BundleProduct} product
   * @param {string|null} selectedSecondary
   * @param {string|null} selectedSize
   * @returns {string|null}
   */
  #getSelectedProductImage(product, selectedSecondary, selectedSize) {
    if (selectedSecondary) {
      const img = this.#getSwatchImage(product, selectedSize, selectedSecondary);
      if (img) return img;
    }
    return product.featuredImageUrl ?? null;
  }

  /**
   * Resolves a variant ID from size and secondary option.
   * @param {BundleProduct} product
   * @param {string|null} selectedSize
   * @param {string|null} secondaryOption
   * @returns {number|null}
   */
  #resolveVariant(product, selectedSize, secondaryOption) {
    const sizeIdx = this.#getSizeOptionIndex(product);
    const secondaryIdx = this.#getSecondaryOptionIndex(product);

    const match = product.variants.find((v) => {
      if (selectedSize && sizeIdx !== -1 && !this.#sizeMatches(v.options[sizeIdx], selectedSize)) return false;
      if (secondaryOption && secondaryIdx !== -1 && v.options[secondaryIdx] !== secondaryOption)
        return false;
      return true;
    });

    return match?.id ?? null;
  }

  // ─── Validation and price ────────────────────────────────────────────────────

  /**
   * Returns true if all required components have a resolved variantId.
   * @param {Object.<string, ComponentSelection|null>} [selections]
   * @returns {boolean}
   */
  #validateRequiredSelections(selections = this.#state.selections) {
    if (!this.#config || !this.#state.selectedSize) return false;
    for (const comp of this.#config.components) {
      if (!comp.required) continue;
      const sel = selections[comp.key];
      if (!sel?.variantId) return false;
    }
    return true;
  }

  /**
   * Computes full and discounted bundle prices from current selections.
   * @returns {{ fullPrice: number, bundlePrice: number }}
   */
  #computePrice() {
    if (!this.#config) return { fullPrice: 0, bundlePrice: 0 };
    let fullPrice = 0;

    for (const comp of this.#config.components) {
      const sel = this.#state.selections[comp.key];
      if (!sel?.variantId) continue;
      const product = comp.products.find((p) => p.id === sel.productId);
      const variant = product?.variants.find((v) => v.id === sel.variantId);
      if (variant) fullPrice += variant.price * sel.quantity;
    }

    const { discountType = 'percent', discountValue = '', discountTiers = '' } = this.#config;
    let bundlePrice = fullPrice;

    if (discountType === 'fixed') {
      // Fixed dollar amount off — discountValue is in dollars, price is in cents
      const fixedOff = parseFloat(discountValue) * 100;
      bundlePrice = Math.max(0, Math.round(fullPrice - fixedOff));
    } else if (discountType === 'tiered' && discountTiers) {
      // Tiered: pick highest matching tier based on total item quantity
      const totalQty = Object.values(this.#state.selections).reduce(
        (sum, sel) => sum + (sel?.quantity ?? 0), 0
      );
      const tiers = discountTiers
        .split(',')
        .map((t) => { const [q, p] = t.trim().split(':'); return { qty: parseInt(q, 10), pct: parseFloat(p) }; })
        .filter((t) => !isNaN(t.qty) && !isNaN(t.pct))
        .sort((a, b) => b.qty - a.qty);
      const matched = tiers.find((t) => totalQty >= t.qty);
      if (matched) bundlePrice = Math.round(fullPrice * (1 - matched.pct / 100));
    } else {
      // Percent (default)
      const pct = parseFloat(discountValue) || 0;
      bundlePrice = Math.round(fullPrice * (1 - pct / 100));
    }

    return { fullPrice, bundlePrice };
  }

  // ─── Pack-presets mode helpers ──────────────────────────────────────────────
  // CARILOHA: everything below supports uiMode === 'pack_presets' only. None of it is
  // called from the default step flow, and #addBundleToCart is untouched — these helpers
  // only ever populate the same `selections` shape the step flow already produces, so the
  // add-to-cart / discount-app behavior is identical either way.

  /**
   * Parses "3:10,6:15,9:20" into [{qty,pct}, ...] sorted ascending by qty.
   * @returns {{qty: number, pct: number}[]}
   */
  #parseTiers() {
    const raw = this.#config?.discountTiers ?? '';
    if (!raw) return [];
    return raw
      .split(',')
      .map((t) => {
        const [q, p] = t.trim().split(':');
        return { qty: parseInt(q, 10), pct: parseFloat(p) };
      })
      .filter((t) => !isNaN(t.qty) && !isNaN(t.pct))
      .sort((a, b) => a.qty - b.qty);
  }

  /**
   * Which product a given pair index resolves to under a color preset.
   * 'solid:N' pins every pair to the Nth color; 'mixed' rotates evenly through all of them.
   * @param {number} idx
   * @param {string} preset - 'mixed' or 'solid:{index}'
   * @param {BundleComponent} comp
   * @returns {BundleProduct|null}
   */
  #presetProductFor(idx, preset, comp) {
    if (!comp?.products?.length) return null;
    if (preset === 'mixed') return comp.products[idx % comp.products.length];
    const solidIdx = Number(String(preset).split(':')[1]);
    return comp.products[Number.isInteger(solidIdx) ? solidIdx : 0] ?? comp.products[0];
  }

  /**
   * Builds a full `selections` object for the first `count` pair components under the given
   * color preset — the pack-presets equivalent of the step flow's per-component selection.
   * Components beyond `count` are simply omitted (same as an explicit skip to #addBundleToCart).
   * @param {number} count
   * @param {string} preset - 'mixed' or 'solid:{index}'
   * @param {string|null} selectedSize
   * @returns {Object.<string, ComponentSelection>}
   */
  #buildPresetSelections(count, preset, selectedSize) {
    const selections = {};
    if (!this.#config) return selections;
    this.#config.components.forEach((comp, idx) => {
      if (idx >= count) return;
      const product = this.#presetProductFor(idx, preset, comp);
      if (!product) return;
      const effSize = this.#effectiveSize(comp, selectedSize);
      const variantId = selectedSize ? this.#resolveVariant(product, effSize, null) : null;
      selections[comp.key] = { productId: product.id, secondaryOption: null, variantId, quantity: comp.defaultQuantity };
    });
    return selections;
  }

  /**
   * Preview pricing for a hypothetical pair count + preset, without touching state — used to
   * show each pack-size card's price before it's clicked. Picks each component's cheapest
   * available variant as a representative price (garment size may not be chosen yet).
   * @param {number} n
   * @param {string} preset - 'mixed' or 'solid:{index}'
   * @returns {{ fullPrice: number, bundlePrice: number }}
   */
  #computePriceForCount(n, preset) {
    if (!this.#config) return { fullPrice: 0, bundlePrice: 0 };
    let fullPrice = 0;
    this.#config.components.forEach((comp, idx) => {
      if (idx >= n) return;
      const product = this.#presetProductFor(idx, preset, comp);
      const variant = product?.variants.find((v) => v.available) ?? product?.variants[0];
      if (variant) fullPrice += variant.price;
    });

    const tiers = [...this.#parseTiers()].sort((a, b) => b.qty - a.qty);
    const matched = tiers.find((t) => n >= t.qty);
    const bundlePrice = matched ? Math.round(fullPrice * (1 - matched.pct / 100)) : fullPrice;
    return { fullPrice, bundlePrice };
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  #render() {
    if (this.#config?.uiMode === 'pack_presets') {
      this.#renderPresetUI();
      return;
    }

    const root = this.refs?.configuratorRoot;
    if (!root || !this.#config) return;

    const { selectedSize, selections, loading, error, addedSuccess } = this.#state;
    const { sizeOptionLabel, sizeOptions, components } = this.#config;
    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;

    const parts = [];

    // ── Size selector ────────────────────────────────────────────────────────
    parts.push(`
      <div class="bc-size-selector bc-card">
        <div class="bc-step-header">
          <span class="bc-step-badge${selectedSize ? ' bc-step-badge--done' : ''}">
            ${selectedSize ? this.#checkSmallIcon() : '1'}
          </span>
          <h2 class="bc-step-heading">Bed Size</h2>
          ${selectedSize ? `<span class="bc-step-done-label">${this.#esc(selectedSize)}</span>` : ''}
        </div>
        <div class="bc-size-options" role="group" aria-label="Select bed size">
          ${sizeOptions.map((size) => `
            <button
              class="bc-size-btn${selectedSize === size ? ' bc-size-btn--selected' : ''}"
              on:click="bundle-configurator-component/handleSizeSelect?size=${encodeURIComponent(size)}"
              aria-pressed="${selectedSize === size ? 'true' : 'false'}"
              type="button"
            >${this.#esc(size)}</button>
          `).join('')}
        </div>
      </div>
    `);

    // ── Component sections ───────────────────────────────────────────────────
    // Compute status for each section: locked | active | upcoming | complete
    let foundActive = false;
    const sectionStatuses = {};
    for (const comp of components) {
      if (!selectedSize) {
        sectionStatuses[comp.key] = 'locked';
      } else {
        const sel = selections[comp.key];
        const isComplete = !!sel?.variantId || sel === null;
        if (isComplete) {
          sectionStatuses[comp.key] = 'complete';
        } else if (!foundActive) {
          sectionStatuses[comp.key] = 'active';
          foundActive = true;
        } else {
          sectionStatuses[comp.key] = 'upcoming';
        }
      }
    }

    components.forEach((comp, idx) => {
      // Step number: size is step 1, components are 2, 3, 4...
      parts.push(this.#renderComponentSection(comp, idx + 2, sectionStatuses[comp.key]));
    });

    // ── Bundle summary card + add-to-cart ───────────────────────────────────
    const { fullPrice, bundlePrice } = this.#computePrice();
    const canAdd = this.#validateRequiredSelections();
    const justBecameReady = canAdd && !this.#wasReady;
    this.#wasReady = canAdd;
    const hasPrice = fullPrice > 0;
    const savings = fullPrice - bundlePrice;

    // Build summary rows for every required component (placeholder if not yet resolved)
    // plus any optional component the shopper has actually selected — e.g. a chosen pillow.
    const summaryRows = components
      .filter((comp) => comp.required || !!selections[comp.key]?.variantId)
      .map((comp) => {
        const sel = selections[comp.key];
        const product = sel?.productId ? comp.products.find((p) => p.id === sel.productId) : null;
        const resolved = !!sel?.variantId;
        const image = resolved && sel.secondaryOption
          ? (this.#getSwatchImage(product, this.#effectiveSize(comp, selectedSize), sel.secondaryOption) ?? product?.featuredImageUrl)
          : product?.featuredImageUrl ?? null;
        const name = product
          ? [product.title, sel?.secondaryOption].filter(Boolean).join(' — ')
          : comp.label;
        const qty = sel?.quantity ?? comp.defaultQuantity;
        return { image, name, resolved, qty };
      });

    parts.push(`
      <div class="bc-summary">
        <div class="bc-summary-items">
          ${summaryRows.map((row) => `
            <div class="bc-summary-item${row.resolved ? '' : ' bc-summary-item--pending'}">
              ${row.image
                ? `<img src="${this.#esc(row.image)}" alt="" class="bc-summary-item__img" width="48" height="48" loading="lazy">`
                : `<span class="bc-summary-item__img-placeholder"></span>`}
              <span class="bc-summary-item__name">${this.#esc(row.name)}${row.qty > 1 ? ` <span class="bc-summary-item__qty">&times;${row.qty}</span>` : ''}</span>
            </div>
          `).join('')}
        </div>
        <div class="bc-price-table">
          <div class="bc-price-row">
            <span>Full Price</span>
            ${hasPrice ? `<s class="bc-price-original">${formatMoney(fullPrice, moneyFormat, currency)}</s>` : `<span class="bc-price-placeholder">&mdash;</span>`}
          </div>
          <div class="bc-price-row">
            <span>Savings</span>
            ${hasPrice ? `<span class="bc-price-savings">${formatMoney(savings, moneyFormat, currency)}</span>` : `<span class="bc-price-placeholder">&mdash;</span>`}
          </div>
          <div class="bc-price-row bc-price-row--total">
            <span>Your Price</span>
            ${hasPrice ? `<span class="bc-price-bundle">${formatMoney(bundlePrice, moneyFormat, currency)}</span>` : `<span class="bc-price-placeholder">&mdash;</span>`}
          </div>
        </div>
        ${error ? `<p class="bc-error" role="alert">${this.#esc(error)}</p>` : ''}
        ${addedSuccess ? `
          <p class="bc-success" role="status">
            <span class="bc-success__icon" aria-hidden="true">${this.#checkIcon()}</span>
            <span>Bundle added to cart</span>
          </p>
        ` : ''}
        <button
          type="button"
          class="button bc-add-btn"
          on:click="bundle-configurator-component/handleAddToCart"
          ${!canAdd || loading ? 'disabled aria-disabled="true"' : ''}
          ${loading ? 'aria-busy="true"' : ''}
        >
          <span class="add-to-cart-text">
            <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">${this.#addToCartIcon()}</span>
            <span class="add-to-cart-text__content">
              <span>
                <span>${loading ? 'Adding to cart&hellip;' : 'Add Bundle to Cart'}</span>
              </span>
            </span>
          </span>
        </button>
      </div>
    `);

    root.innerHTML = parts.join('');

    // Trigger button pulse AFTER paint so the animation fires on an in-DOM element
    if (justBecameReady) {
      requestAnimationFrame(() => {
        const btn = root.querySelector('.bc-add-btn');
        if (!btn) return;
        btn.classList.add('bc-add-btn--pulse');
        btn.addEventListener('animationend', () => btn.classList.remove('bc-add-btn--pulse'), { once: true });
      });
    }
  }

  /**
   * CARILOHA: pack-presets mode render — pack-size cards, two color-preset cards (solid /
   * mixed), the same size selector and Add to Cart button as the step flow. State shape and
   * #addBundleToCart are shared with the step flow; only the picker UI differs.
   */
  #renderPresetUI() {
    const root = this.refs?.configuratorRoot;
    if (!root || !this.#config) return;

    const { selectedSize, loading, error, addedSuccess } = this.#state;
    const { sizeOptionLabel, sizeOptions, components } = this.#config;
    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;

    const tiers = this.#parseTiers();
    const midIdx = Math.floor((tiers.length - 1) / 2);
    const pairCount = this.#state.pairCount ?? tiers[midIdx]?.qty ?? components.filter((c) => c.required).length;
    const colorPreset = this.#state.colorPreset ?? 'mixed';

    // CARILOHA: broadcast pack size / color / discount to any listening bundle-media-gallery
    // (the custom media grid that replaces the standard gallery on this template) —
    // decoupled via a document-level event rather than either component reaching into the
    // other's DOM.
    const activeTier = tiers.find((t) => t.qty === pairCount);
    document.dispatchEvent(
      new CustomEvent('cariloha:bundle-state', { detail: { pairCount, colorPreset, pct: activeTier?.pct ?? null } })
    );

    const { fullPrice, bundlePrice } = this.#computePrice();
    const hasPrice = fullPrice > 0;
    const savings = fullPrice - bundlePrice;
    const savingsPct = fullPrice > 0 ? Math.round((savings / fullPrice) * 100) : 0;
    const perPair = pairCount > 0 && hasPrice ? Math.round(bundlePrice / pairCount) : 0;

    const canAdd = this.#validateRequiredSelections();
    const justBecameReady = canAdd && !this.#wasReady;
    this.#wasReady = canAdd;

    root.innerHTML = `
      <div class="bc-preset bc-card">
        <div class="bc-preset-price-row">
          ${hasPrice
            ? `<span class="bc-preset-price-now">${formatMoney(bundlePrice, moneyFormat, currency)}</span>`
            : `<span class="bc-preset-price-now bc-price-placeholder">&mdash;</span>`}
          ${hasPrice && savings > 0 ? `
            <span class="bc-preset-price-was">${formatMoney(fullPrice, moneyFormat, currency)}</span>
            <span class="bc-preset-price-save">Save ${formatMoney(savings, moneyFormat, currency)} &middot; ${savingsPct}%</span>
          ` : ''}
        </div>
        ${hasPrice ? `<div class="bc-preset-price-per-unit">${formatMoney(perPair, moneyFormat, currency)} per pair</div>` : ''}

        <div class="bc-preset-field">
          <span class="bc-preset-label">How Many Pairs</span>
          <div class="bc-pack-options" role="group" aria-label="How many pairs" style="--bc-pack-count:${tiers.length || 1};">
            ${tiers
              .map((tier, i) => {
                const preview = this.#computePriceForCount(tier.qty, colorPreset);
                const pressed = pairCount === tier.qty;
                return `
                  <button
                    type="button"
                    class="bc-pack-card"
                    aria-pressed="${pressed}"
                    on:click="bundle-configurator-component/handlePairCountSelect?count=${tier.qty}"
                  >
                    ${i === midIdx ? `<span class="bc-pack-badge">Most Popular</span>` : ''}
                    <span class="bc-pack-name">${tier.qty}-Pack</span>
                    <span class="bc-pack-amount">${formatMoney(preview.bundlePrice, moneyFormat, currency)}</span>
                    <span class="bc-pack-off">Save ${tier.pct}%</span>
                  </button>
                `;
              })
              .join('')}
          </div>
        </div>

        <div class="bc-preset-field">
          <span class="bc-preset-label">Color</span>
          <div class="bc-preset-options" role="group" aria-label="Color">
            ${this.#renderColorPresetOptions(colorPreset)}
          </div>
        </div>

        <div class="bc-preset-field">
          <span class="bc-preset-label">${this.#esc(sizeOptionLabel || 'Size')}</span>
          <div class="bc-size-options" role="group" aria-label="Select ${this.#esc(sizeOptionLabel || 'size')}">
            ${sizeOptions
              .map(
                (size) => `
              <button
                class="bc-size-btn${selectedSize === size ? ' bc-size-btn--selected' : ''}"
                on:click="bundle-configurator-component/handleSizeSelect?size=${encodeURIComponent(size)}"
                aria-pressed="${selectedSize === size ? 'true' : 'false'}"
                type="button"
              >${this.#esc(size)}</button>
            `
              )
              .join('')}
          </div>
        </div>

        ${error ? `<p class="bc-error" role="alert">${this.#esc(error)}</p>` : ''}
        ${addedSuccess ? `
          <p class="bc-success" role="status">
            <span class="bc-success__icon" aria-hidden="true">${this.#checkIcon()}</span>
            <span>Bundle added to cart</span>
          </p>
        ` : ''}

        <button
          type="button"
          class="button bc-add-btn"
          on:click="bundle-configurator-component/handleAddToCart"
          ${!canAdd || loading ? 'disabled aria-disabled="true"' : ''}
          ${loading ? 'aria-busy="true"' : ''}
        >
          <span class="add-to-cart-text">
            <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">${this.#addToCartIcon()}</span>
            <span class="add-to-cart-text__content">
              <span>
                <span>${loading ? 'Adding to cart&hellip;' : 'Add Bundle to Cart'}</span>
              </span>
            </span>
          </span>
        </button>
      </div>
    `;

    if (justBecameReady) {
      requestAnimationFrame(() => {
        const btn = root.querySelector('.bc-add-btn');
        if (!btn) return;
        btn.classList.add('bc-add-btn--pulse');
        btn.addEventListener('animationend', () => btn.classList.remove('bc-add-btn--pulse'), { once: true });
      });
    }
  }

  /**
   * Short display label for a color preset card — strips a shared product-title prefix
   * (e.g. "Bamboo Boxer Briefs - Black" → "Black") rather than showing the full title.
   * @param {BundleProduct} product
   * @returns {string}
   */
  #shortColorLabel(product) {
    if (!product?.title) return '';
    const parts = product.title.split(' - ');
    return parts.length > 1 ? parts[parts.length - 1].trim() : product.title;
  }

  /**
   * Renders every color-preset card for the current bundle: one solid-color card per product
   * covered by `solidPresetCount` (in component order), then the Neutral mix of all of them.
   * A single-solid bundle (e.g. Boxer Bundle, solidPresetCount 1) renders the original 2-card
   * layout; a bundle with more standalone colors (e.g. a socks bundle with 2) renders 3+.
   * @param {string} activePreset - 'mixed' or 'solid:{index}'
   * @returns {string}
   */
  #renderColorPresetOptions(activePreset) {
    const colors = this.#config?.components?.[0]?.products ?? [];
    const solidCount = Math.min(this.#config?.solidPresetCount ?? 1, colors.length);
    const cards = [];
    for (let i = 0; i < solidCount; i++) {
      cards.push(this.#renderSolidPresetCard(i, colors[i], activePreset));
    }
    cards.push(this.#renderMixedPresetCard(colors, activePreset));
    return cards.join('');
  }

  /**
   * @param {number} idx
   * @param {BundleProduct} product
   * @param {string} activePreset
   * @returns {string}
   */
  #renderSolidPresetCard(idx, product, activePreset) {
    const preset = `solid:${idx}`;
    const pressed = activePreset === preset;
    return `
      <button
        type="button"
        class="bc-preset-card"
        aria-pressed="${pressed}"
        on:click="bundle-configurator-component/handleColorPresetSelect?preset=${encodeURIComponent(preset)}"
      >
        <span class="bc-preset-swatch">${product?.featuredImageUrl ? `<img src="${this.#esc(product.featuredImageUrl)}" alt="" loading="lazy">` : ''}</span>
        <span class="bc-preset-cname">${this.#esc(this.#shortColorLabel(product) || 'Solid')}</span>
      </button>
    `;
  }

  /**
   * @param {BundleProduct[]} colors
   * @param {string} activePreset
   * @returns {string}
   */
  #renderMixedPresetCard(colors, activePreset) {
    const pressed = activePreset === 'mixed';
    return `
      <button
        type="button"
        class="bc-preset-card"
        aria-pressed="${pressed}"
        on:click="bundle-configurator-component/handleColorPresetSelect?preset=mixed"
      >
        <span class="bc-preset-swatch bc-preset-swatch--cluster">
          ${colors.map((p) => (p.featuredImageUrl ? `<img src="${this.#esc(p.featuredImageUrl)}" alt="" loading="lazy">` : '')).join('')}
        </span>
        <span class="bc-preset-cname">Neutral</span>
      </button>
    `;
  }

  /**
   * Renders a component section card.
   * @param {BundleComponent} comp
   * @param {number} stepNum
   * @param {'locked'|'active'|'upcoming'|'complete'} status
   * @returns {string}
   */
  #renderComponentSection(comp, stepNum, status) {
    const { selectedSize, selections } = this.#state;
    const effSize = this.#effectiveSize(comp, selectedSize);
    const sel = selections[comp.key];
    const isSkipped = sel === null;
    const isComplete = status === 'complete';
    const isLocked = status === 'locked';

    // Determine reveal animation — plays once per section per size-selection
    const needsReveal = selectedSize && !this.#revealedSections.has(comp.key);
    if (needsReveal) {
      requestAnimationFrame(() => this.#revealedSections.add(comp.key));
    }

    const classes = [
      'bc-section',
      'bc-card',
      `bc-section--${status}`,
      isSkipped ? 'bc-section--skipped' : '',
      needsReveal ? 'bc-section--reveal' : '',
    ].filter(Boolean).join(' ');

    const revealStyle = needsReveal
      ? ` style="animation-delay: ${(stepNum - 2) * 80}ms"`
      : '';

    const parts = [];

    parts.push(`<div class="${classes}" data-comp="${this.#esc(comp.key)}"${revealStyle}>`);

    // ── Completed chip (collapsed) ───────────────────────────────────────────
    if (isComplete) {
      const product = comp.products.find((p) => p.id === sel.productId);
      const chipImage = sel.secondaryOption
        ? (this.#getSwatchImage(product, effSize, sel.secondaryOption) ?? product?.featuredImageUrl)
        : product?.featuredImageUrl;
      const chipLabel = [product?.title, sel.secondaryOption].filter(Boolean).join(' — ');
      // CARILOHA: skip the Change action when there's nothing to switch to (e.g. a pillow with
      // only one color) — otherwise it clears the resolved variant and disables Add to Cart
      // until the shopper re-clicks the same single option, which isn't obvious.
      const canChange = this.#hasAlternativeChoice(comp, effSize);
      parts.push(`
        <div class="bc-chip">
          <span class="bc-step-badge bc-step-badge--done">${this.#checkSmallIcon()}</span>
          ${chipImage ? `<img src="${this.#esc(chipImage)}" alt="" class="bc-chip__img">` : ''}
          <div class="bc-chip__info">
            <span class="bc-chip__step-name">${this.#esc(comp.label)}</span>
            <span class="bc-chip__selection">${this.#esc(chipLabel)}</span>
          </div>
          ${canChange ? `
            <button
              type="button"
              class="bc-chip__change"
              on:click="bundle-configurator-component/handleEditSection?compKey=${encodeURIComponent(comp.key)}"
            >Change</button>
          ` : ''}
        </div>
      `);
      parts.push('</div>');
      return parts.join('');
    }

    // ── Section header ───────────────────────────────────────────────────────
    parts.push(`
      <div class="bc-step-header">
        <span class="bc-step-badge">${stepNum}</span>
        <h3 class="bc-section-title">
          ${this.#esc(comp.label)}
          ${!comp.required ? '<span class="bc-optional-tag">(optional)</span>' : ''}
        </h3>
      </div>
    `);

    // ── Locked state ─────────────────────────────────────────────────────────
    if (isLocked) {
      parts.push(`<p class="bc-locked-msg">Select your bed size above to continue</p>`);
      parts.push('</div>');
      return parts.join('');
    }

    // ── Full interactive section ──────────────────────────────────────────────
    if (!isSkipped) {
      // Product card grid
      parts.push(`
        <div class="bc-product-grid" role="group" aria-label="Choose ${this.#esc(comp.label)}">
          ${comp.products.map((product) => {
            const isSelected = sel?.productId === product.id;
            const available = product.variants.some(
              (v) => v.available && (!effSize || this.#variantMatchesSize(v, product, effSize))
            );
            const priceVariant = product.variants.find(
              (v) => v.available && (!effSize || this.#variantMatchesSize(v, product, effSize))
            ) ?? product.variants[0];
            const cardImage = (isSelected && sel?.secondaryOption)
              ? (this.#getSwatchImage(product, effSize, sel.secondaryOption) ?? product.featuredImageUrl)
              : product.featuredImageUrl;
            return `
              <button
                class="bc-product-card${isSelected ? ' bc-product-card--selected' : ''}${!available ? ' bc-product-card--unavailable' : ''}"
                on:click="bundle-configurator-component/handleProductSelect?compKey=${encodeURIComponent(comp.key)}&productId=${product.id}"
                type="button"
                aria-pressed="${isSelected ? 'true' : 'false'}"
                ${!available ? 'disabled aria-disabled="true"' : ''}
              >
                <span class="bc-product-card__img-wrap">
                  ${cardImage
                    ? `<img src="${this.#esc(cardImage)}" alt="" class="bc-product-card__img" width="160" height="160" loading="lazy">`
                    : `<span class="bc-product-card__img-placeholder"></span>`}
                  ${isSelected ? `<span class="bc-product-card__check" aria-hidden="true">${this.#checkIcon()}</span>` : ''}
                </span>
                <span class="bc-product-card__name">${this.#esc(product.title)}</span>
                ${priceVariant ? `<span class="bc-product-card__price">${formatMoney(priceVariant.price, this.dataset.moneyFormat, this.dataset.currency)}</span>` : ''}
              </button>
            `;
          }).join('')}
        </div>
      `);

      // Secondary option picker — swatches for a visual Color option, text pills for
      // anything else (e.g. mattress "Firmness Option": Soft/Firm has no meaningful image).
      if (sel?.productId != null) {
        const product = comp.products.find((p) => p.id === sel.productId);
        if (product) {
          const secondaryValues = this.#getSecondaryOptionValues(product, effSize);
          const optionName = this.#getSecondaryOptionName(product);
          const isColor = this.#isColorOption(optionName);
          if (secondaryValues && optionName) {
            parts.push(`
              <div class="bc-color-picker">
                <p class="bc-color-label">
                  ${this.#esc(optionName)}
                  ${sel.secondaryOption ? `<span class="bc-color-selected">${this.#esc(sel.secondaryOption)}</span>` : ''}
                </p>
                <div class="${isColor ? 'bc-swatches' : 'bc-text-options'}" role="group" aria-label="Select ${this.#esc(optionName)}">
                  ${secondaryValues.map((value) => {
                    const isSelectedSwatch = sel.secondaryOption === value;
                    const variantId = this.#resolveVariant(product, effSize, value);
                    const matchVariant = product.variants.find((v) => v.id === variantId);
                    const available = matchVariant?.available !== false;
                    if (!isColor) {
                      return `
                        <button
                          class="bc-size-btn${isSelectedSwatch ? ' bc-size-btn--selected' : ''}"
                          on:click="bundle-configurator-component/handleSecondarySelect?compKey=${encodeURIComponent(comp.key)}&value=${encodeURIComponent(value)}"
                          type="button"
                          aria-pressed="${isSelectedSwatch ? 'true' : 'false'}"
                          ${!available ? 'disabled aria-disabled="true"' : ''}
                        >${this.#esc(value)}</button>
                      `;
                    }
                    const swatchImage = this.#getSwatchImage(product, effSize, value);
                    return `
                      <button
                        class="bc-swatch${isSelectedSwatch ? ' bc-swatch--selected' : ''}${!available ? ' bc-swatch--unavailable' : ''}"
                        on:click="bundle-configurator-component/handleSecondarySelect?compKey=${encodeURIComponent(comp.key)}&value=${encodeURIComponent(value)}"
                        type="button"
                        aria-pressed="${isSelectedSwatch ? 'true' : 'false'}"
                        aria-label="${this.#esc(value)}"
                        title="${this.#esc(value)}"
                        ${!available ? 'disabled aria-disabled="true"' : ''}
                      >
                        <span
                          class="bc-swatch__circle"
                          ${swatchImage ? `style="background-image: url('${this.#esc(swatchImage)}')"` : ''}
                          aria-hidden="true"
                        ></span>
                      </button>
                    `;
                  }).join('')}
                </div>
              </div>
            `);
          }
        }
      }

      // Quantity
      if (comp.allowQuantityChange && sel?.variantId != null) {
        parts.push(`
          <div class="bc-quantity">
            <span class="bc-quantity-label">Quantity:</span>
            <div class="bc-quantity-controls">
              <button type="button" class="bc-quantity-btn"
                on:click="bundle-configurator-component/handleQuantityDecrement?compKey=${encodeURIComponent(comp.key)}"
                aria-label="Decrease quantity" ${sel.quantity <= 1 ? 'disabled' : ''}>&minus;</button>
              <span class="bc-quantity-value">${sel.quantity}</span>
              <button type="button" class="bc-quantity-btn"
                on:click="bundle-configurator-component/handleQuantityIncrement?compKey=${encodeURIComponent(comp.key)}"
                aria-label="Increase quantity">&plus;</button>
            </div>
          </div>
        `);
      }
    }

    // Skip / restore for optional
    if (!comp.required) {
      if (isSkipped) {
        parts.push(`
          <button type="button" class="bc-restore-btn"
            on:click="bundle-configurator-component/handleRestoreComponent?compKey=${encodeURIComponent(comp.key)}"
          >+ Add ${this.#esc(comp.label)}</button>
        `);
      } else {
        parts.push(`
          <button type="button" class="bc-skip-btn"
            on:click="bundle-configurator-component/handleSkipComponent?compKey=${encodeURIComponent(comp.key)}"
          >Don&rsquo;t include ${this.#esc(comp.label)}</button>
        `);
      }
    }

    parts.push('</div>');
    return parts.join('');
  }

  // ─── Add to cart ─────────────────────────────────────────────────────────────

  async #addBundleToCart() {
    this.#setState({ loading: true, error: null, addedSuccess: false });

    const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const { selectedSize, selections } = this.#state;
    const productTitle = this.dataset.productTitle;

    // Collect all cart-items-component section IDs for Section Rendering API
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    if (!this.#config) return;

    // Build the items array
    const discountType = this.#config.discountType || 'percent';
    const discountValue = this.#config.discountValue || '';
    const discountTiers = this.#config.discountTiers || '';

    // CARILOHA: group selections by resolved variantId before building cart items, so
    // multiple component slots that resolve to the identical variant (e.g. several
    // pack-presets "pairs" all set to the same color) collapse into one cart line item
    // with a summed quantity, instead of one line item per slot. For bundles where every
    // component is always a distinct product (bedding bundles), each variantId is already
    // unique, so this produces exactly one group per component — no behavior change there.
    /** @type {Map<number, {variantId: number, quantity: number, comp: BundleComponent, secondaryOption: string|null}>} */
    const grouped = new Map();
    for (const comp of this.#config.components) {
      const sel = selections[comp.key];
      if (!sel?.variantId) continue; // Unselected or explicitly skipped

      const existing = grouped.get(sel.variantId);
      if (existing) {
        existing.quantity += sel.quantity;
      } else {
        grouped.set(sel.variantId, {
          variantId: sel.variantId,
          quantity: sel.quantity,
          comp,
          secondaryOption: sel.secondaryOption,
        });
      }
    }

    const items = [];
    for (const { variantId, quantity, comp, secondaryOption } of grouped.values()) {
      /** @type {Record<string, string>} */
      const properties = {
        _bundle_id: bundleId,
        _bundle_component: comp.key,
        _bundle_label: comp.label,
        _bundle_parent: productTitle ?? '',
        _bundle_size: selectedSize ?? '',
        _bundle_discount_type: discountType,
      };
      if (discountValue) properties._bundle_discount_value = discountValue;
      if (discountTiers) properties._bundle_tiers = discountTiers;
      if (secondaryOption) properties._bundle_color = secondaryOption;

      items.push({
        id: variantId,
        quantity,
        properties,
      });
    }

    if (items.length === 0) {
      this.#setState({ loading: false, error: 'Please complete your bundle selections.' });
      return;
    }

    const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);

    const deferredAddPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferredAddPromise.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const addData = await addResponse.json();

      /* CARILOHA: /cart/add.js's response has no top-level `currency` field (only
         full-cart endpoints like /cart.js and /cart/update.js do), but
         createCartFromAjaxResponse requires one to format line item prices —
         fetch the full cart afterward, matching the pattern in product-form.js's
         own #refreshCart(). */
      const currentCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());

      if (addData.status) {
        // Shopify returned a 422 error
        deferredAddPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
          detail: {
            didError: true,
            source: 'bundle-configurator-component',
            itemCount: totalQuantity,
          },
        });
        this.#setState({ loading: false, error: addData.message || 'Could not add bundle to cart.' });
        return;
      }

      // Success — resolves the dispatched CartLinesUpdateEvent so the cart drawer opens and sections re-render
      deferredAddPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart),
        detail: {
          source: 'bundle-configurator-component',
          itemCount: totalQuantity,
          sections: addData.sections,
          didError: false,
        },
      });

      this.#setState({ loading: false, addedSuccess: true });

      // Announce to screen readers
      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'Bundle added to cart.';
        setTimeout(() => {
          this.refs.liveRegion.textContent = '';
        }, 5000);
      }

      // Clear success message after 4 seconds
      setTimeout(() => {
        this.#setState({ addedSuccess: false });
      }, 4000);
    } catch (err) {
      console.error('[bundle-configurator] Add to cart failed:', err);
      deferredAddPromise.reject(err);
      this.#setState({ loading: false, error: 'Network error. Please try again.' });
    }
  }

  // ─── Utilities ───────────────────────────────────────────────────────────────

  /**
   * Scrolls smoothly to the first incomplete required section, or to the summary when all done.
   * Uses window.scrollTo so we can subtract the sticky header height.
   */
  #scrollToNextSection() {
    if (!this.#config) return;
    const { selections } = this.#state;

    let target = null;
    for (const comp of this.#config.components) {
      if (!comp.required) continue;
      const sel = selections[comp.key];
      const isComplete = !!sel?.variantId || sel === null;
      if (!isComplete) {
        target = this.querySelector(`[data-comp="${comp.key}"]`);
        break;
      }
    }

    if (!target) {
      target = this.querySelector('.bc-summary');
    }

    this.#smoothScrollTo(target);
  }

  /**
   * Scrolls smoothly to an element, offsetting for the sticky header.
   * CARILOHA: as of Horizon 20260918, .page-wrapper becomes the actual scrolling
   * container at desktop widths (>=990px) rather than window/document (see
   * .page-wrapper's overflow-y: auto in base.css, part of the drawer "squeeze"
   * layout). window.scrollTo() is a no-op there, so this finds whichever element
   * is actually scrollable — .page-wrapper on desktop, window on narrower
   * viewports where .page-wrapper has no overflow — and scrolls that one.
   * @param {Element|null} el
   */
  #smoothScrollTo(el) {
    if (!el) return;
    const headerHeight = this.#getStickyHeaderHeight();
    const gap = 16;
    const pageWrapper = document.querySelector('.page-wrapper');
    const pageWrapperScrolls = pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight;

    if (pageWrapperScrolls) {
      const top =
        el.getBoundingClientRect().top -
        pageWrapper.getBoundingClientRect().top +
        pageWrapper.scrollTop -
        headerHeight -
        gap;
      pageWrapper.scrollTo({ top, behavior: 'smooth' });
    } else {
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - gap;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  /**
   * Returns the sticky header clearance in pixels.
   * Hardcoded to 75px per Cariloha header height.
   * @returns {number}
   */
  #getStickyHeaderHeight() {
    return 75;
  }

  /**
   * Returns true if a variant matches the given size (already translated through
   * the owning component's sizeOverrides, if any — see #effectiveSize).
   * @param {BundleVariant} variant
   * @param {BundleProduct} product
   * @param {string|null} selectedSize
   * @returns {boolean}
   */
  #variantMatchesSize(variant, product, selectedSize) {
    if (!selectedSize) return true;
    const sizeIdx = this.#getSizeOptionIndex(product);
    if (sizeIdx === -1) return true;
    return this.#sizeMatches(variant.options[sizeIdx], selectedSize);
  }

  /**
   * Translates the bundle's selected size into the size this component's product
   * actually sells under, via the component's sizeOverrides map. Used when a
   * component's variants don't use the same size vocabulary as the bundle (e.g.
   * Retreat Comfort Base only sells a "Split-King" variant, which also covers
   * "King" bundle orders — one physical base serves both).
   * @param {BundleComponent} comp
   * @param {string|null} selectedSize
   * @returns {string|null}
   */
  #effectiveSize(comp, selectedSize) {
    if (!selectedSize) return selectedSize;
    return comp?.sizeOverrides?.[selectedSize] ?? selectedSize;
  }

  /**
   * Normalised size comparison.
   *
   * Handles three cases beyond a simple equality check:
   *   1. Composite variant sizes — "Full/Queen" or "King/Cal-King" — match if
   *      the selected bundle size equals any slash-delimited segment.
   *   2. "Standard" pillow size — treated as matching any non-King bundle size
   *      (per Cariloha product setup: Standard pillow fits Twin/Full/Queen bundles).
   *   3. Exact match (case-insensitive) — always passes.
   *
   * @param {string} variantSizeValue - The size string stored on the variant option
   * @param {string} bundleSize - The bundle size the customer selected
   * @returns {boolean}
   */
  #sizeMatches(variantSizeValue, bundleSize) {
    if (!variantSizeValue || !bundleSize) return true;
    const vLower = variantSizeValue.toLowerCase();
    const bLower = bundleSize.toLowerCase();
    // Exact match
    if (vLower === bLower) return true;
    // Composite size like "Full/Queen" or "King/Cal-King"
    if (variantSizeValue.includes('/')) {
      return variantSizeValue.split('/').some((seg) => seg.trim().toLowerCase() === bLower);
    }
    // "Standard" pillow = any non-King bundle size
    if (vLower === 'standard') {
      return bLower !== 'king';
    }
    return false;
  }

  /**
   * Escapes a string for safe HTML insertion.
   * @param {string} str
   * @returns {string}
   */
  #esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Same icon as the theme's standard add-to-cart button (assets/icon-add-to-cart.svg). */
  #addToCartIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width)" d="M14.608 12.563v5m2.5-2.5h-5"/></svg>`;
  }

  #checkIcon() {
    return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  #checkSmallIcon() {
    return `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  #chevronIcon() {
    return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }
}

if (!customElements.get('bundle-configurator-component')) {
  customElements.define('bundle-configurator-component', BundleConfiguratorComponent);
}
